import { CheckoutPlan, PlanIntervalUnit } from "lib/types/subscriptions";
import { hexToRgba } from "lib/utils/helpers";

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const formatUnit = (unit: PlanIntervalUnit, count: number, t: TranslateFn) =>
  t(`subscription_interval_${unit.toLowerCase()}`, { count });

export const formatFrequency = (plan: CheckoutPlan, t: TranslateFn) => {
  const unit = formatUnit(plan.intervalUnit, plan.intervalCount, t);
  return plan.intervalCount === 1
    ? unit
    : t("subscription_count_with_unit", { count: plan.intervalCount, unit });
};

export const formatDuration = (plan: CheckoutPlan, t: TranslateFn) => {
  if (!plan.maxCycles) return null;
  const span = plan.maxCycles;
  return t("subscription_count_with_unit", { count: span, unit: formatUnit(plan.intervalUnit, span, t) });
};

export const deliveryCount = (plan: CheckoutPlan) => {
  const derived = plan.maxCycles ? Math.floor(plan.maxCycles / plan.intervalCount) : null;
  return plan.pricing.deliveryCount ?? derived;
};

export const formatDeliveries = (plan: CheckoutPlan, t: TranslateFn) => {
  const count = deliveryCount(plan);
  if (!count) return null;
  return t("subscription_deliveries", { count });
};

export const formatSavings = (plan: CheckoutPlan, t: TranslateFn) => {
  const { savings, total, originalTotal } = plan.pricing;
  const amount =
    savings ?? (originalTotal !== undefined && total !== undefined ? originalTotal - total : undefined);

  if (!amount || amount <= 0) return null;
  return t("subscription_savings_amount", { amount: amount.toLocaleString("en-IN") });
};

export const formatDiscount = (plan: CheckoutPlan, t: TranslateFn) => {
  if (plan.discountType === "NONE" || !plan.discountValue) return null;
  return plan.discountType === "PERCENTAGE"
    ? t("subscription_discount_percentage", { value: plan.discountValue })
    : t("subscription_discount_flat", { value: plan.discountValue });
};

export const formatBillingDate = (value: string | number | undefined): string | null => {
  if (value === undefined || value === null || value === "") return null;

  const numeric = typeof value === "number" ? value : Number(value);
  // Epoch seconds are ~10 digits, millis ~13. Below the threshold, scale up.
  const timestamp = Number.isFinite(numeric)
    ? numeric < 1e11
      ? numeric * 1000
      : numeric
    : Date.parse(String(value));

  if (!Number.isFinite(timestamp)) return null;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  return formatDayMonth(date);
};

const formatDayMonth = (date: Date): string =>
  date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const addInterval = (from: Date, count: number, unit: PlanIntervalUnit): Date => {
  const next = new Date(from.getTime());

  if (unit === "WEEK") {
    next.setDate(next.getDate() + count * 7);
    return next;
  }

  const day = next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + count);
  const lastDayOfMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(day, lastDayOfMonth));
  return next;
};

export interface ScheduleNoteTemplates {
  firstOrder: string;
  nextOrder: string;
  ends: string;
  untilCancelled: string;
  delivery: string;
}

const NUDGE_TEXT_EDGE = "rgba(0, 0, 0, 0.7)";

/** Plan-card defaults, used until the merchant overrides the colour on the payment page. */
export const DEFAULT_NUDGE = "#FFC400";
export const DEFAULT_RING = "#FFD23D";
export const DEFAULT_STRIP_TEXT = "#5E3F02";
export const DEFAULT_OFFER_PILL = "linear-gradient(90deg, rgba(0, 153, 102, 0.71) 0%, #009966 100%), #FFFFFF";

const withAlpha = (color: string, alpha: number) => hexToRgba(color.substring(0, 7), alpha, "transparent");

export const nudgeTextGradient = (color?: string): string =>
  `linear-gradient(90deg, ${NUDGE_TEXT_EDGE} 0%, ${color || DEFAULT_NUDGE} 56.25%, ${NUDGE_TEXT_EDGE} 100%)`;

/* Both fall back to the ring colour, so an unconfigured merchant gets the gold everywhere. */
export const ribbonGradient = (color?: string): string => {
  const base = color || DEFAULT_RING;

  return `linear-gradient(90deg, ${base} 0%, ${withAlpha(base, 0)} 100%)`;
};

export const highlightGradient = (color?: string): string => {
  const base = color || DEFAULT_RING;

  return `linear-gradient(90deg, ${withAlpha(base, 0.2)} 0%, ${base} 49.52%, ${withAlpha(base, 0.2)} 100%)`;
};

const fillTemplate = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);

export const formatScheduleNote = (plan: CheckoutPlan, lines: string[], t: TranslateFn): string[] => {
  const frequency = formatFrequency(plan, t);
  const count = deliveryCount(plan);
  const today = new Date();

  const nextDate =
    formatBillingDate(plan.pricing.nextBillingDate) ??
    formatDayMonth(addInterval(today, plan.intervalCount, plan.intervalUnit));

  const endDate =
    plan.maxCycles === null
      ? null
      : formatBillingDate(plan.pricing.endDate) ??
        formatDayMonth(addInterval(today, plan.maxCycles - plan.intervalCount, plan.intervalUnit));

  return lines
    .filter(Boolean)
    .map((line) => {
      if (line.includes("{duration_date}") && !endDate) return "";

      return fillTemplate(line, {
        frequency,
        date_of_order: formatDayMonth(today),
        upcoming_order: nextDate,
        duration_date: endDate ?? "",
        delivery_count: count === null ? "" : String(count),
      });
    })
    .filter(Boolean);
};
